import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";
import { Button, Popconfirm, Progress, message } from "antd";
import {
  CheckCircle,
  CircleNotch,
  Clock,
  Sparkle,
  WarningCircle,
  XCircle,
} from "@phosphor-icons/react";
import {
  AnalysisActionRow,
  AnalysisElapsedTime,
  AnalysisErrorBanner,
  AnalysisHelperText,
  AnalysisProgressBarWrapper,
  AnalysisProgressContainer,
  AnalysisProgressHeaderRow,
  AnalysisProgressSectionHeader,
  AnalysisStatusPill,
  AnalysisStatusPillDot,
  ConditionList,
  ConditionRow,
  ConditionRowMeta,
  ConditionRowName,
  ConditionStatusIconWrap,
} from "./AIPaneStyled";
import {
  cancelAnalysisJob,
  isTerminalJobStatus,
  triggerAnalysisJob,
} from "../../slice/AISlice";

/**
 * Progress view for an in-flight, failed, or cancelled attorney
 * analysis job. Renders three blocks:
 *
 *   1. Header — status pill + elapsed time
 *   2. Progress bar — `completed_conditions / total_conditions` with
 *      a tabular-nums counter
 *   3. Per-condition checklist — one row per discovered condition,
 *      with an icon that maps directly to the per-condition `status`
 *      field (pending / analyzing / completed / failed)
 *
 * For failed jobs, also renders an error banner + Try Again button.
 *
 * Note: completed jobs (`completed`, `completed_with_errors`) are
 * routed by AnalysisTab to the Phase 4 `AnalysisReport` component
 * directly — they never reach this view in practice.
 *
 * The polling lives in `usePollAnalysisJob` (mounted higher up by
 * AnalysisTab) — this component is a pure render of the cached job
 * state. That separation keeps the component testable without a
 * fake clock + interval.
 */
const STATUS_LABEL = {
  queued: "Queued",
  running: "Analyzing",
  completed: "Completed",
  completed_with_errors: "Completed with errors",
  failed: "Failed",
  cancelled: "Cancelled",
};

const WORKFLOW_PHASE_LABEL = {
  queued: "Waiting for the Brief worker to start.",
  discovering_conditions: "Reading the matter graph and finding conditions.",
  analyzing_conditions: "Analyzing the discovered conditions.",
  compiling_report: "Compiling the Brief report.",
  generating_overview: "Generating the strategic overview.",
  completed: "Brief completed.",
  failed: "Brief failed.",
  cancelled: "Brief was cancelled.",
};

const formatElapsed = (createdAtMs, nowMs) => {
  if (!createdAtMs || !nowMs || nowMs < createdAtMs) return "0s";
  const totalSec = Math.floor((nowMs - createdAtMs) / 1000);
  const min = Math.floor(totalSec / 60);
  const sec = totalSec % 60;
  if (min === 0) return `${sec}s`;
  return `${min}m ${String(sec).padStart(2, "0")}s`;
};

const ConditionStatusIcon = ({ status: rowStatus }) => {
  if (rowStatus === "completed") {
    return (
      <ConditionStatusIconWrap $status="completed" aria-label="Completed">
        <CheckCircle size={16} weight="fill" />
      </ConditionStatusIconWrap>
    );
  }
  if (rowStatus === "failed") {
    return (
      <ConditionStatusIconWrap $status="failed" aria-label="Failed">
        <XCircle size={16} weight="fill" />
      </ConditionStatusIconWrap>
    );
  }
  if (rowStatus === "analyzing") {
    return (
      <ConditionStatusIconWrap $status="analyzing" aria-label="Analyzing">
        {/*
          Phosphor doesn't ship an animated spinner; reuse `CircleNotch`
          and add a CSS spin via inline style so we don't have to add
          a global keyframes import outside AIPaneStyled.
         */}
        <CircleNotch
          size={16}
          weight="bold"
          style={{ animation: "ai-spin 1s linear infinite" }}
        />
      </ConditionStatusIconWrap>
    );
  }
  return (
    <ConditionStatusIconWrap $status="pending" aria-label="Pending">
      <Clock size={14} weight="bold" />
    </ConditionStatusIconWrap>
  );
};
ConditionStatusIcon.propTypes = { status: PropTypes.string };
ConditionStatusIcon.defaultProps = { status: "pending" };

// One-time keyframes injection (no global stylesheet edit) — keeps
// the spinning condition-row icon working even if styled-components'
// keyframes API isn't reachable from a plain SVG icon. Idempotent.
const installSpinKeyframesOnce = (() => {
  let installed = false;
  return () => {
    if (installed || typeof document === "undefined") return;
    const styleEl = document.createElement("style");
    styleEl.setAttribute("data-ai-spin-keyframes", "true");
    styleEl.textContent =
      "@keyframes ai-spin { to { transform: rotate(360deg); } }";
    document.head.appendChild(styleEl);
    installed = true;
  };
})();

const AnalysisProgressView = ({ documentId, matterId, job, isTriggering }) => {
  const dispatch = useDispatch();
  const [now, setNow] = useState(() => Date.now());
  const [isCancelling, setIsCancelling] = useState(false);

  useEffect(() => {
    installSpinKeyframesOnce();
  }, []);

  // Live elapsed-time clock: tick once per second while the job is
  // running. Stops as soon as the job hits a terminal status so we
  // freeze on the final duration. Independent of the API poll
  // interval so the user sees the seconds tick smoothly between
  // 5s polls instead of jumping in 5s chunks.
  const isRunning = job?.status === "running";
  useEffect(() => {
    if (!isRunning) return undefined;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [isRunning]);

  const createdAtMs = useMemo(() => {
    if (!job?.created_at) return null;
    const ms = Date.parse(job.created_at);
    return Number.isNaN(ms) ? null : ms;
  }, [job?.created_at]);

  const completedAtMs = useMemo(() => {
    if (!job?.completed_at) return null;
    const ms = Date.parse(job.completed_at);
    return Number.isNaN(ms) ? null : ms;
  }, [job?.completed_at]);

  const elapsedEnd = isRunning ? now : completedAtMs || now;
  const elapsedLabel = formatElapsed(createdAtMs, elapsedEnd);

  const total =
    typeof job?.total_conditions === "number" ? job.total_conditions : 0;
  const completed =
    typeof job?.completed_conditions === "number"
      ? job.completed_conditions
      : 0;
  const failed =
    typeof job?.failed_conditions === "number" ? job.failed_conditions : 0;
  const percent =
    total > 0 ? Math.min(100, Math.round((completed / total) * 100)) : 0;

  const conditions = Array.isArray(job?.conditions) ? job.conditions : [];
  // Stable, predictable order for the checklist. The serializer
  // already orders by `sequence_number` but defensive sort here keeps
  // the UI deterministic if the backend ever changes ordering and a
  // poll arrives mid-stream.
  const sortedConditions = useMemo(() => {
    return [...conditions].sort((a, b) => {
      const aSeq = typeof a?.sequence_number === "number" ? a.sequence_number : 0;
      const bSeq = typeof b?.sequence_number === "number" ? b.sequence_number : 0;
      return aSeq - bSeq;
    });
  }, [conditions]);

  const jobStatus = job?.status || "running";
  const isFailed = jobStatus === "failed" || jobStatus === "cancelled";
  // Queued (BE PR #1898): the job exists but no worker is executing —
  // it starts automatically when the user's running Brief frees a
  // slot. Rendered without the progress bar / discovery copy / elapsed
  // clock, since all of those imply active analysis.
  const isQueued = jobStatus === "queued";
  const isTerminal = isTerminalJobStatus(jobStatus);
  const workflowPhaseText =
    job?.workflow_phase_message ||
    WORKFLOW_PHASE_LABEL[job?.workflow_phase] ||
    "";

  const handleRetry = () => {
    if (!documentId) return;
    // `matterId` rides along so the optimistic synthetic job lands in
    // the matter cache — the matter-first AI pane renders from there.
    dispatch(triggerAnalysisJob({ documentId, claimedOnly: true, matterId }));
  };

  const handleCancel = async () => {
    if (!documentId || job?.id == null || isCancelling) return;
    setIsCancelling(true);
    try {
      await dispatch(cancelAnalysisJob({ documentId, jobId: job.id })).unwrap();
      message.success("Brief cancelled.");
    } catch (err) {
      message.error(err?.message || "Couldn't cancel the Brief.", 6);
    } finally {
      setIsCancelling(false);
    }
  };

  return (
    <AnalysisProgressContainer data-testid="ai-analysis-progress">
      <AnalysisProgressHeaderRow>
        <AnalysisStatusPill $status={jobStatus} data-testid="ai-status-pill">
          <AnalysisStatusPillDot />
          {STATUS_LABEL[jobStatus] || jobStatus}
        </AnalysisStatusPill>
        {/* No elapsed clock while queued — nothing is executing yet,
            and "Total 0s" would read as a stalled run. */}
        {!isQueued && (
          <AnalysisElapsedTime data-testid="ai-elapsed-time">
            {isRunning ? "Elapsed" : "Total"} {elapsedLabel}
          </AnalysisElapsedTime>
        )}
      </AnalysisProgressHeaderRow>

      {isQueued ? (
        // Queued: the job's own workflow message ("Queued — starts
        // automatically when your running Brief finishes.") IS the
        // progress message. No progress bar, no "Discovering
        // conditions…" — those imply a worker is executing, and none
        // is. Deliberately NOT `workflowPhaseText`: its phase-label
        // fallback maps workflow_phase="queued" to "Waiting for the
        // Brief worker to start.", which describes a dispatched job,
        // not one held behind the concurrency cap.
        <AnalysisHelperText data-testid="ai-queued-message">
          {job?.workflow_phase_message ||
            "Queued — starts automatically when your running Brief finishes."}
        </AnalysisHelperText>
      ) : (
        <div>
          <AnalysisProgressSectionHeader>
            {total > 0
              ? `Analyzing ${total} condition${total === 1 ? "" : "s"}`
              : "Discovering conditions…"}
            {failed > 0 && (
              <>
                {" "}
                <AnalysisProgressCountsInline data-testid="ai-failed-count">
                  ({failed} failed)
                </AnalysisProgressCountsInline>
              </>
            )}
          </AnalysisProgressSectionHeader>
          <AnalysisProgressBarWrapper>
            <Progress
              percent={percent}
              size="small"
              showInfo
              format={() => `${completed} / ${total > 0 ? total : "?"}`}
              status={isFailed ? "exception" : "active"}
              aria-label={`Analysis progress: ${completed} of ${total} conditions complete`}
            />
          </AnalysisProgressBarWrapper>
          {workflowPhaseText && (
            <AnalysisHelperText data-testid="ai-workflow-phase">
              Worker status: {workflowPhaseText}
            </AnalysisHelperText>
          )}
        </div>
      )}

      {sortedConditions.length === 0 ? (
        !isQueued && (
          <AnalysisHelperText>
            Conditions will appear here as the AI discovers them. This
            usually takes the first 10–20 seconds.
          </AnalysisHelperText>
        )
      ) : (
        <ConditionList data-testid="ai-condition-list">
          {/*
            The row is the CONDITION NAME — "Irritable Bowel Syndrome",
            "Headaches" — plus its VA status. The codesheet's "claimed
            as" alias pile deliberately does NOT render here: on real
            matters it runs to a dozen wordings and crushed the name out
            of its own row (Matthew, 2026-08-19 + Whitley 2026-08-23).
            Those wordings belong in the full Brief, where each
            condition's section shows them as chips.
          */}
          {sortedConditions.map((cond) => (
            <ConditionRow
              key={cond.id || cond.condition_name}
              data-testid="ai-condition-row"
              data-condition-status={cond.status || "pending"}
            >
              <ConditionRowName title={cond.condition_name}>
                {cond.condition_name || "Unnamed condition"}
              </ConditionRowName>
              {cond.condition_status && (
                <ConditionRowMeta>{cond.condition_status}</ConditionRowMeta>
              )}
              <ConditionStatusIcon status={cond.status} />
            </ConditionRow>
          ))}
        </ConditionList>
      )}

      {isFailed && job?.error_message && (
        <AnalysisErrorBanner role="alert">
          <WarningCircle
            size={14}
            weight="fill"
            style={{ marginRight: 6, verticalAlign: "middle" }}
          />
          {job.error_message}
        </AnalysisErrorBanner>
      )}

      {isFailed && (
        <AnalysisActionRow>
          <Button
            type="primary"
            icon={<Sparkle size={14} weight="fill" />}
            onClick={handleRetry}
            loading={isTriggering}
            disabled={!documentId || isTriggering}
          >
            Try again
          </Button>
        </AnalysisActionRow>
      )}

      {!isTerminal && (
        <AnalysisActionRow>
          <Popconfirm
            title="Cancel this Brief?"
            description="This stops the analysis immediately, even mid-condition. Conditions already completed are kept."
            okText="Cancel Brief"
            okType="danger"
            cancelText="Keep running"
            onConfirm={handleCancel}
            disabled={isCancelling}
          >
            <Button
              danger
              icon={<XCircle size={14} weight="fill" />}
              loading={isCancelling}
              disabled={job?.id == null || isCancelling}
              data-testid="ai-cancel-button"
            >
              Cancel
            </Button>
          </Popconfirm>
        </AnalysisActionRow>
      )}

      {!isTerminal && (
        <AnalysisHelperText>
          Analysis runs on the backend — you can switch tabs, open
          another document, or close this panel and come back. Progress
          is preserved.
        </AnalysisHelperText>
      )}
    </AnalysisProgressContainer>
  );
};

// Inline counts span — defined here so we don't need yet another
// styled export for a single use site. Lazy import-style avoidance
// of a circular dep with AIPaneStyled.
const AnalysisProgressCountsInline = ({ children, ...props }) => (
  <span
    {...props}
    style={{ fontSize: "12px", color: "#7688A3", fontWeight: 500, marginLeft: 4 }}
  >
    {children}
  </span>
);
AnalysisProgressCountsInline.propTypes = { children: PropTypes.node };

AnalysisProgressView.propTypes = {
  documentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  matterId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  job: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    status: PropTypes.string,
    workflow_phase: PropTypes.string,
    workflow_phase_message: PropTypes.string,
    workflow_heartbeat_at: PropTypes.string,
    total_conditions: PropTypes.number,
    completed_conditions: PropTypes.number,
    failed_conditions: PropTypes.number,
    created_at: PropTypes.string,
    completed_at: PropTypes.string,
    error_message: PropTypes.string,
    conditions: PropTypes.array,
  }),
  isTriggering: PropTypes.bool,
};

AnalysisProgressView.defaultProps = {
  documentId: null,
  matterId: null,
  job: null,
  isTriggering: false,
};

export default AnalysisProgressView;
